import type {
  CaptureMethod,
  EquipmentStructuredData,
  GenerateMagicLinkResponse,
  MagicLinkPurpose,
  PaginatedResponse,
  RedeemMagicLinkResponse,
} from '@bower/shared';
import type { HttpClient } from './http-client.types';

export interface CreateEquipmentBody {
  title: string;
  make?: string;
  model?: string;
  serial?: string;
  location?: string;
  photoUrl?: string;
  scanConfidence?: number;
  /** Destination. Omit for the workspace root. Must be sent as a pair. */
  parentEntityType?: 'workspace' | 'project';
  parentEntityId?: string;
  /**
   * Capture provenance. `captureSurface` is deliberately absent: the equipment
   * controller resolves it at the door from `x-bower-client`
   * (`resolveCaptureSurface`), and a body value would beat that header — the
   * per-call-site guess this migration removed.
   */
  captureMethod?: CaptureMethod;
}

export interface UpdateEquipmentBody {
  title?: string;
  make?: string;
  model?: string;
  serial?: string;
  location?: string;
  notes?: string;
}

export function listEquipment(http: HttpClient, params: Record<string, string | number> = {}) {
  const base = '/equipment';
  if (!params || Object.keys(params).length === 0)
    return http.get<
      PaginatedResponse<{
        id: string;
        title: string | null;
        artifactType: string;
        structuredData: EquipmentStructuredData | null;
        updatedAt: string;
      }>
    >(base);
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  return http.get<
    PaginatedResponse<{
      id: string;
      title: string | null;
      artifactType: string;
      structuredData: EquipmentStructuredData | null;
      updatedAt: string;
    }>
  >(qs ? `${base}?${qs}` : base);
}

export function getEquipment(http: HttpClient, id: string) {
  return http.get<{
    id: string;
    title: string | null;
    artifactType: string;
    markdown: string | null;
    structuredData: EquipmentStructuredData | null;
    knowledgeArtifacts: Array<{
      id: string;
      title: string | null;
      artifactType: string;
      createdAt: string;
    }>;
    createdAt: string;
    updatedAt: string;
  }>(`/equipment/${id}`);
}

export function createEquipment(http: HttpClient, body: CreateEquipmentBody) {
  return http.request<{ id: string; gri: string }>(
    { method: 'POST', url: '/equipment', data: body },
    0,
  );
}

export function updateEquipment(http: HttpClient, id: string, body: UpdateEquipmentBody) {
  return http.patch<{ id: string }>(`/equipment/${id}`, body);
}

/**
 * Client deadline for the equipment nameplate scan.
 *
 * MUST be >= the SERVER's `SCAN_TIMEOUT_MS` (60s, in
 * `backend/src/controllers/equipment.controller.ts`), plus room for the
 * base64 image upload and the response. A client that gives up FIRST can only
 * ever destroy work the server was going to deliver -- and it does not abort
 * the request, so Vertex still generates and Google still bills for it.
 *
 * Until 2026-09-03 this call set nothing and inherited the global axios
 * `timeout: 30000` from api.service.ts -- HALF the server's own considered
 * deadline. The inversion was invisible because the server-side sample that
 * justified 60s counted `status=200` rows, which by construction cannot
 * contain a scan the browser abandoned.
 *
 * MEASURED 2026-09-03, production Cloud Logging, 30 days, n=57:
 *   median 6.9s - p95 38.7s - max (200) 53.9s
 *   5 of 57 (8.8%) exceeded the old 30s client ceiling
 *   3 of those SUCCEEDED server-side at 38.7s / 43.8s / 53.9s -- work the
 *     browser threw away and Google billed for
 *   2 hit the server's own 60s ceiling
 *
 * So 75s = the server's 60s hard stop + ~15s for upload and network. It is NOT
 * a model-latency budget: the server always terminates at 60s, so this only
 * has to outlast that plus the round trip. Re-measure before changing it, and
 * re-stamp this comment -- an unsourced ceiling cannot be audited, which is how
 * the 30s survived three months (#2007, and the same class as #1976 / #1983).
 *
 * RETRIES ARE OFF, and that is load-bearing for the number above. `http.post`
 * defaults to `retries = 1`, and `shouldRetry` fires on BOTH `ECONNABORTED`
 * (our own timeout) and `status >= 500` (which is what the server's own 60s
 * deadline returns). With retries on, "75s" would really mean up to ~150s of
 * user-visible wait AND a SECOND full model call — so raising the ceiling would
 * have doubled the wasted, still-billed work this change exists to stop.
 *
 * A scan that reaches 75s has already had the server give up at 60s. Retrying
 * spends another full Vertex call on the path the server itself abandoned; a
 * fast clear failure is better, and is what the affected user did by hand
 * anyway. `request(..., 0)` is the existing idiom for this (attachments.api.ts).
 *
 * The ordering is pinned by scripts/__tests__/equipment-scan-deadline-chain.test.mjs,
 * which reads BOTH constants rather than restating either, and also pins that
 * retries stay off — otherwise the ceiling documented here silently doubles.
 */
const SCAN_CLIENT_TIMEOUT_MS = 75_000;

export function scanEquipmentPhoto(http: HttpClient, imageBase64: string) {
  return http.request<{
    make: string;
    model: string;
    serial: string;
    confidence: number;
    rawText: string;
  }>(
    {
      method: 'POST',
      url: '/equipment/scan',
      data: { imageBase64 },
      timeout: SCAN_CLIENT_TIMEOUT_MS,
    },
    0,
  );
}

export function retriggerEquipmentKnowledgeFetch(http: HttpClient, id: string) {
  return http.post<{ id: string }>(`/equipment/${id}/knowledge-fetch`, {});
}

export function uploadEquipmentPhoto(http: HttpClient, id: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return http.request<{ photoUrl: string }>({
    method: 'PATCH',
    url: `/equipment/${id}/photo`,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
}

export function searchEquipmentOnline(http: HttpClient, query: string) {
  return http.post<Array<{ make: string; model: string; title: string; description: string }>>(
    '/equipment/search-online',
    { query },
  );
}

// Both responses use the shared contract rather than a hand-rolled inline
// shape: a local subset compiles fine while silently omitting whatever the
// server actually returns (this is how `purpose` went missing on redeem).
export function generateMagicLink(http: HttpClient, purpose: MagicLinkPurpose) {
  return http.post<GenerateMagicLinkResponse>('/auth/magic-link', { purpose });
}

export function redeemMagicLink(http: HttpClient, token: string) {
  return http.post<RedeemMagicLinkResponse>('/auth/magic-link/redeem', { token });
}
